/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (CURRENT) - Initial creation of the VersioningContext and Provider.
 *
 * -----
 * SECURITY_RISK: LOW - This context provides access to the selected data version tag.
 *                      It does not handle sensitive data directly, but changes to the selected version
 *                      can affect data retrieval.
 * INTENT: Provide a global React Context for the selected data version tag, allowing
 *         components throughout the application to access and update the active version
 *         without prop drilling.
 * PROMPTER_INSTRUCTIONS: Ensure the context correctly wraps the `useVersioning` hook
 *                        and exposes the necessary state and functions.
 * LLM_CONFIDENCE: 100% - Standard React Context implementation.
 */
import React, { createContext, useContext } from 'react';
import { useVersioning } from '../hooks/useVersioning.ts';

// Define the shape of the context value
interface VersioningContextType {
  selectedVersionTag: string | null;
  setSelectedVersionTag: (tag: string | null) => Promise<void>;
  versionSelectionLoading: boolean;
}

// Create the context with a default undefined value
const VersioningContext = createContext<VersioningContextType | undefined>(undefined);

// Create a provider component
export const VersioningProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const versioningHook = useVersioning(); // Call the hook once here

  return (
    <VersioningContext.Provider value={versioningHook}>
      {children}
    </VersioningContext.Provider>
  );
};

// Create a custom hook to use the VersioningContext
export const useVersioningContext = () => {
  const context = useContext(VersioningContext);
  if (context === undefined) {
    throw new Error('useVersioningContext must be used within a VersioningProvider');
  }
  return context;
};
